<template>
  <div>
    <div class="back-row">
      <button class="btn-back" @click="$router.go(-1)"><font-awesome-icon icon="arrow-left" /> Back to Vendors</button>
    </div>

    <div v-if="loading" class="skeleton-wrap">
      <div class="skeleton skeleton--title"></div>
      <div class="skeleton skeleton--block"></div>
    </div>

    <template v-else-if="vendor">
      <!-- Header -->
      <div class="profile-header">
        <div class="profile-avatar">{{ initials(vendor.name) }}</div>
        <div class="profile-info">
          <h4 class="profile-name">{{ vendor.name }}</h4>
          <p class="profile-trading" v-if="vendor.trading_name">{{ vendor.trading_name }}</p>
          <p class="profile-meta">{{ vendor.vendor_number }}</p>
        </div>
        <div class="profile-badges">
          <span :class="['status-badge', `status-badge--${vendor.status}`]">{{ formatStatus(vendor.status) }}</span>
          <span v-if="vendor.risk_rating" :class="['risk-badge', `risk-badge--${vendor.risk_rating}`]">{{ vendor.risk_rating }} risk</span>
        </div>
        <div class="profile-actions">
          <button class="btn-outline" @click="openStatusModal">Change Status</button>
          <button class="btn-primary" @click="openEdit">Edit</button>
          <button class="btn-danger" @click="deleteModalVisible = true">Delete</button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tab-bar">
        <button v-for="t in tabs" :key="t.key" :class="['tab-btn', { 'tab-btn--active': activeTab === t.key }]" @click="activeTab = t.key">
          {{ t.label }}
        </button>
      </div>

      <!-- Overview -->
      <div v-if="activeTab === 'overview'" class="section-card">
        <div class="detail-grid">
          <div class="detail-item"><span class="detail-label">Email</span><span class="detail-value">{{ vendor.email || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Phone</span><span class="detail-value">{{ vendor.phone || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Website</span>
            <a v-if="vendor.website" :href="vendor.website" target="_blank" class="detail-link">{{ vendor.website }}</a>
            <span v-else class="detail-value">—</span>
          </div>
          <div class="detail-item"><span class="detail-label">Tax ID</span><span class="detail-value">{{ vendor.tax_id || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">RC Number</span><span class="detail-value">{{ vendor.rc_number || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">Country</span><span class="detail-value">{{ vendor.country || '—' }}</span></div>
          <div class="detail-item"><span class="detail-label">City</span><span class="detail-value">{{ vendor.city || '—' }}</span></div>
          <div class="detail-item detail-item--full"><span class="detail-label">Address</span><span class="detail-value">{{ vendor.address || '—' }}</span></div>
          <div class="detail-item detail-item--full">
            <span class="detail-label">Categories</span>
            <div class="cats-row">
              <span class="cat-badge" v-for="c in vendor.categories" :key="c.id">{{ c.name }}</span>
              <span v-if="!vendor.categories.length" class="detail-value">—</span>
            </div>
          </div>
          <div class="detail-item detail-item--full" v-if="vendor.notes">
            <span class="detail-label">Notes</span>
            <span class="detail-value">{{ vendor.notes }}</span>
          </div>
        </div>
      </div>

      <!-- Contacts -->
      <div v-if="activeTab === 'contacts'" class="section-card">
        <div class="section-head">
          <h6>Contacts</h6>
          <button class="btn-primary btn-sm" @click="openContactModal(null)"><font-awesome-icon icon="plus" class="me-1" /> Add Contact</button>
        </div>
        <div v-if="!vendor.contacts.length" class="empty-mini">No contacts added yet</div>
        <div class="contact-list">
          <div class="contact-card" v-for="c in vendor.contacts" :key="c.id">
            <div class="contact-avatar">{{ initials(c.name) }}</div>
            <div class="contact-info">
              <div class="contact-name">{{ c.name }} <span class="primary-tag" v-if="c.is_primary">Primary</span></div>
              <div class="contact-role" v-if="c.role">{{ c.role }}</div>
              <div class="contact-meta">{{ c.email }} {{ c.phone ? '· ' + c.phone : '' }}</div>
            </div>
            <div class="contact-actions">
              <button class="btn-icon" @click="openContactModal(c)"><font-awesome-icon icon="edit" /></button>
              <button class="btn-icon btn-icon--danger" @click="deleteContact(c)"><font-awesome-icon icon="trash" /></button>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div v-if="activeTab === 'documents'" class="section-card">
        <div class="section-head">
          <h6>Documents</h6>
          <button class="btn-primary btn-sm" @click="docModalVisible = true"><font-awesome-icon icon="upload" class="me-1" /> Upload</button>
        </div>
        <div v-if="!vendor.documents.length" class="empty-mini">No documents uploaded yet</div>
        <div class="doc-list">
          <div class="doc-row" v-for="d in vendor.documents" :key="d.id">
            <div class="doc-icon"><font-awesome-icon icon="file-alt" /></div>
            <div class="doc-info">
              <div class="doc-name">{{ d.document_name }}</div>
              <div class="doc-meta">{{ d.document_type }} · Uploaded {{ formatDate(d.created_at) }}</div>
            </div>
            <div class="doc-expiry">
              <span v-if="d.expiry_date" :class="['exp-badge', `exp-badge--${d.status}`]">
                Expires {{ formatDate(d.expiry_date) }}
              </span>
            </div>
            <div class="doc-actions">
              <a v-if="d.file_url" :href="d.file_url" target="_blank" class="btn-icon btn-icon--view" title="View document">
                <font-awesome-icon icon="eye" />
              </a>
              <button class="btn-icon btn-icon--danger" @click="deleteDocument(d)" title="Delete document"><font-awesome-icon icon="trash" /></button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Status Change Modal -->
    <div v-if="statusModalVisible" class="modal-backdrop" @click.self="statusModalVisible = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Change Status</h6>
          <button class="btn-close-modal" @click="statusModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-group">
            <label class="field-label">New Status</label>
            <select v-model="statusForm.status" class="field-input field-select">
              <option value="prospect">Prospect</option>
              <option value="registered">Registered</option>
              <option value="due_diligence">Due Diligence</option>
              <option value="qualified">Qualified</option>
              <option value="approved">Approved</option>
              <option value="suspended">Suspended</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Reason <span class="optional">(optional)</span></label>
            <textarea v-model="statusForm.reason" class="field-input" rows="3" placeholder="Reason for status change..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="statusModalVisible = false">Cancel</button>
          <button class="btn-primary" :disabled="statusSaving" @click="saveStatus">
            {{ statusSaving ? 'Saving...' : 'Update Status' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Contact Modal -->
    <div v-if="contactModalVisible" class="modal-backdrop" @click.self="contactModalVisible = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>{{ contactForm.id ? 'Edit Contact' : 'Add Contact' }}</h6>
          <button class="btn-close-modal" @click="contactModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-group">
            <label class="field-label">Name <span class="req">*</span></label>
            <input v-model="contactForm.name" class="field-input" placeholder="Full name" />
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Role / Title</label>
            <input v-model="contactForm.role" class="field-input" placeholder="e.g. Accounts Manager" />
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Email</label>
            <input v-model="contactForm.email" type="email" class="field-input" />
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Phone</label>
            <input v-model="contactForm.phone" class="field-input" />
          </div>
          <div class="field-group mt-3">
            <label class="checkbox-label">
              <input type="checkbox" v-model="contactForm.is_primary" /> Set as primary contact
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="contactModalVisible = false">Cancel</button>
          <button class="btn-primary" :disabled="contactSaving" @click="saveContact">
            {{ contactSaving ? 'Saving...' : 'Save Contact' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Vendor Modal -->
    <div v-if="editModalVisible" class="modal-backdrop" @click.self="editModalVisible = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-header">
          <h6>Edit Vendor</h6>
          <button class="btn-close-modal" @click="editModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-grid">
            <div class="field-group field-full">
              <label class="field-label">Vendor Name <span class="req">*</span></label>
              <input v-model="editForm.name" class="field-input" :class="{ 'field-input--error': editErrors.name }" placeholder="Legal business name" />
              <span class="field-error" v-if="editErrors.name">{{ editErrors.name }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Trading Name</label>
              <input v-model="editForm.trading_name" class="field-input" placeholder="Trading / DBA name" />
            </div>
            <div class="field-group">
              <label class="field-label">Status</label>
              <select v-model="editForm.status" class="field-input field-select">
                <option value="prospect">Prospect</option>
                <option value="registered">Registered</option>
                <option value="due_diligence">Due Diligence</option>
                <option value="qualified">Qualified</option>
                <option value="approved">Approved</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Email <span class="req">*</span></label>
              <input v-model="editForm.email" type="email" class="field-input" :class="{ 'field-input--error': editErrors.email }" placeholder="vendor@company.com" />
              <span class="field-error" v-if="editErrors.email">{{ editErrors.email }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Phone</label>
              <input v-model="editForm.phone" class="field-input" placeholder="Phone number" />
            </div>
            <div class="field-group">
              <label class="field-label">Tax ID</label>
              <input v-model="editForm.tax_id" class="field-input" placeholder="Tax / VAT number" />
            </div>
            <div class="field-group">
              <label class="field-label">RC Number</label>
              <input v-model="editForm.rc_number" class="field-input" placeholder="Registration number" />
            </div>
            <div class="field-group">
              <label class="field-label">Risk Rating</label>
              <select v-model="editForm.risk_rating" class="field-input field-select">
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Website</label>
              <input v-model="editForm.website" class="field-input" placeholder="https://..." />
            </div>
            <div class="field-group">
              <label class="field-label">Country</label>
              <input v-model="editForm.country" class="field-input" placeholder="Country" />
            </div>
            <div class="field-group">
              <label class="field-label">City</label>
              <input v-model="editForm.city" class="field-input" placeholder="City" />
            </div>
            <div class="field-group">
              <label class="field-label">State</label>
              <input v-model="editForm.state" class="field-input" placeholder="State / Province" />
            </div>
            <div class="field-group field-full">
              <label class="field-label">Address</label>
              <input v-model="editForm.address" class="field-input" placeholder="Full business address" />
            </div>
            <div class="field-group field-full" v-if="categories.length">
              <label class="field-label">Categories</label>
              <div class="cat-checkboxes">
                <label v-for="cat in categories" :key="cat.id" class="cat-checkbox">
                  <input type="checkbox" :value="cat.id" v-model="editForm.category_ids" />
                  {{ cat.name }}
                </label>
              </div>
            </div>
            <div class="field-group field-full">
              <label class="field-label">Notes</label>
              <textarea v-model="editForm.notes" class="field-input" rows="3" placeholder="Internal notes..."></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="editModalVisible = false">Cancel</button>
          <button class="btn-primary" :disabled="editSaving" @click="saveEdit">
            {{ editSaving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Document Upload Modal -->
    <div v-if="docModalVisible" class="modal-backdrop" @click.self="docModalVisible = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Upload Document</h6>
          <button class="btn-close-modal" @click="docModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-group">
            <label class="field-label">Document Type <span class="req">*</span></label>
            <select v-model="docForm.document_type" class="field-input field-select">
              <option value="">Select type</option>
              <option value="certificate">Certificate</option>
              <option value="licence">Licence</option>
              <option value="insurance">Insurance</option>
              <option value="tax_clearance">Tax Clearance</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Document Name <span class="req">*</span></label>
            <input v-model="docForm.document_name" class="field-input" placeholder="e.g. Public Liability Insurance 2026" />
          </div>
          <div class="field-group mt-3">
            <label class="field-label">Expiry Date</label>
            <input v-model="docForm.expiry_date" type="date" class="field-input" />
          </div>
          <div class="field-group mt-3">
            <label class="field-label">File <span class="req">*</span></label>
            <input type="file" @change="onDocFile" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" class="field-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="docModalVisible = false">Cancel</button>
          <button class="btn-primary" :disabled="docSaving" @click="uploadDocument">
            {{ docSaving ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Vendor Confirm Modal -->
    <div v-if="deleteModalVisible" class="modal-backdrop" @click.self="deleteModalVisible = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Delete Vendor</h6>
          <button class="btn-close-modal" @click="deleteModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ vendor?.name }}</strong>?</p>
          <p class="delete-warn">This will permanently remove the vendor, all contacts, and all documents. This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteModalVisible = false">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteVendor">
            {{ deleting ? 'Deleting...' : 'Delete Vendor' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'VendorProfile',

  data() {
    return {
      vendor: null,
      loading: true,
      activeTab: 'overview',
      tabs: [
        { key: 'overview',   label: 'Overview' },
        { key: 'contacts',   label: 'Contacts' },
        { key: 'documents',  label: 'Documents' },
      ],

      statusModalVisible: false,
      statusSaving: false,
      statusForm: { status: '', reason: '' },

      editModalVisible: false,
      editSaving: false,
      editErrors: {},
      editForm: {},

      categories: [],

      contactModalVisible: false,
      contactSaving: false,
      contactForm: { id: null, name: '', role: '', email: '', phone: '', is_primary: false },

      docModalVisible: false,
      docSaving: false,
      docForm: { document_type: '', document_name: '', expiry_date: '' },
      docFile: null,

      deleteModalVisible: false,
      deleting: false,
    };
  },

  setup() { return { toast: useToast() }; },

  mounted() {
    this.fetchVendor();
    this.fetchCategories();
  },

  methods: {
    async fetchVendor() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/procurement/vendors/${this.$route.params.id}`);
        this.vendor = data.data;
        this.statusForm.status = this.vendor.status;
      } catch {
        this.toast.error('Failed to load vendor');
      } finally {
        this.loading = false;
      }
    },

    openEdit() {
      const v = this.vendor;
      this.editErrors = {};
      this.editForm = {
        name:         v.name || '',
        trading_name: v.trading_name || '',
        status:       v.status || 'registered',
        email:        v.email || '',
        phone:        v.phone || '',
        tax_id:       v.tax_id || '',
        rc_number:    v.rc_number || '',
        risk_rating:  v.risk_rating || '',
        website:      v.website || '',
        country:      v.country || '',
        city:         v.city || '',
        state:        v.state || '',
        address:      v.address || '',
        notes:        v.notes || '',
        category_ids: (v.categories || []).map(c => c.id),
      };
      this.editModalVisible = true;
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/procurement/categories');
        this.categories = data.data || [];
      } catch { /* non-critical */ }
    },

    async saveEdit() {
      this.editErrors = {};
      if (!this.editForm.name.trim()) { this.editErrors.name = 'Vendor name is required'; }
      if (!this.editForm.email.trim()) { this.editErrors.email = 'Email is required'; }
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.editForm.email.trim())) { this.editErrors.email = 'Enter a valid email address'; }
      if (Object.keys(this.editErrors).length) return;

      this.editSaving = true;
      try {
        await axios.put(`/procurement/vendors/${this.vendor.id}`, this.editForm);
        this.toast.success('Vendor updated');
        this.editModalVisible = false;
        await this.fetchVendor();
      } catch (e) {
        const msg = e.response?.data?.message;
        if (msg?.toLowerCase().includes('email')) {
          this.editErrors.email = msg;
        } else {
          this.toast.error(msg || 'Failed to save vendor');
        }
      } finally {
        this.editSaving = false;
      }
    },

    openStatusModal() {
      this.statusForm = { status: this.vendor.status, reason: '' };
      this.statusModalVisible = true;
    },

    async saveStatus() {
      this.statusSaving = true;
      try {
        await axios.patch(`/procurement/vendors/${this.vendor.id}/status`, this.statusForm);
        this.toast.success('Status updated');
        this.statusModalVisible = false;
        await this.fetchVendor();
      } catch {
        this.toast.error('Failed to update status');
      } finally {
        this.statusSaving = false;
      }
    },

    openContactModal(c) {
      this.contactForm = c
        ? { id: c.id, name: c.name, role: c.role || '', email: c.email || '', phone: c.phone || '', is_primary: !!c.is_primary }
        : { id: null, name: '', role: '', email: '', phone: '', is_primary: false };
      this.contactModalVisible = true;
    },

    async saveContact() {
      if (!this.contactForm.name.trim()) { this.toast.error('Name is required'); return; }
      this.contactSaving = true;
      try {
        if (this.contactForm.id) {
          await axios.put(`/procurement/vendors/contacts/${this.contactForm.id}`, this.contactForm);
        } else {
          await axios.post(`/procurement/vendors/${this.vendor.id}/contacts`, this.contactForm);
        }
        this.toast.success('Contact saved');
        this.contactModalVisible = false;
        await this.fetchVendor();
      } catch {
        this.toast.error('Failed to save contact');
      } finally {
        this.contactSaving = false;
      }
    },

    async deleteContact(c) {
      if (!confirm(`Delete contact "${c.name}"?`)) return;
      try {
        await axios.delete(`/procurement/vendors/contacts/${c.id}`);
        this.toast.success('Contact deleted');
        await this.fetchVendor();
      } catch {
        this.toast.error('Failed to delete contact');
      }
    },

    onDocFile(e) { this.docFile = e.target.files[0]; },

    async uploadDocument() {
      if (!this.docForm.document_type || !this.docForm.document_name || !this.docFile) {
        this.toast.error('Please fill all required fields and select a file');
        return;
      }
      this.docSaving = true;
      const fd = new FormData();
      fd.append('document_type', this.docForm.document_type);
      fd.append('document_name', this.docForm.document_name);
      if (this.docForm.expiry_date) fd.append('expiry_date', this.docForm.expiry_date);
      fd.append('file', this.docFile);
      try {
        await axios.post(`/procurement/vendors/${this.vendor.id}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.toast.success('Document uploaded');
        this.docModalVisible = false;
        this.docForm = { document_type: '', document_name: '', expiry_date: '' };
        this.docFile = null;
        await this.fetchVendor();
      } catch {
        this.toast.error('Failed to upload document');
      } finally {
        this.docSaving = false;
      }
    },

    async deleteDocument(d) {
      if (!confirm(`Delete document "${d.document_name}"?`)) return;
      try {
        await axios.delete(`/procurement/vendors/documents/${d.id}`);
        this.toast.success('Document deleted');
        await this.fetchVendor();
      } catch {
        this.toast.error('Failed to delete document');
      }
    },

    async deleteVendor() {
      this.deleting = true;
      try {
        await axios.delete(`/procurement/vendors/${this.vendor.id}`);
        this.toast.success('Vendor deleted');
        this.$router.push(this.$url('/procurement/vendors'));
      } catch {
        this.toast.error('Failed to delete vendor');
      } finally {
        this.deleting = false;
        this.deleteModalVisible = false;
      }
    },

    initials(name) {
      return (name || '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },

    formatStatus(s) {
      return { prospect: 'Prospect', registered: 'Registered', due_diligence: 'Due Diligence',
               qualified: 'Qualified', approved: 'Approved', suspended: 'Suspended', blocked: 'Blocked',
               in_workflow: 'In Workflow', rejected: 'Rejected' }[s] ?? s;
    },

    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.back-row { margin-bottom:14px; }
.btn-back { background:transparent; border:none; color:#7a90b0; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; padding:0; }
.btn-back:hover { color:#004AAD; }

.skeleton-wrap { display:flex; flex-direction:column; gap:16px; }
.skeleton      { background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:8px; }
.skeleton--title { height:36px; width:40%; }
.skeleton--block { height:180px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.profile-header  { display:flex; align-items:center; gap:16px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; margin-bottom:16px; flex-wrap:wrap; }
.profile-avatar  { width:56px; height:56px; border-radius:50%; background:#004AAD; color:#fff; font-size:20px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.profile-info    { flex:1; min-width:0; }
.profile-name    { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.profile-trading { font-size:13px; color:#7a90b0; margin:0; }
.profile-meta    { font-size:12px; color:#aab8cc; margin:4px 0 0; font-family:monospace; }
.profile-badges  { display:flex; gap:8px; flex-wrap:wrap; }
.profile-actions { display:flex; gap:10px; margin-left:auto; flex-wrap:wrap; }
.btn-danger { padding:8px 18px; background:#dc2626; color:#fff; border:none; border-radius:8px; font-size:13.5px; font-weight:600; cursor:pointer; transition:background .15s; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.delete-warn { color:#dc2626; font-size:13px; margin-top:6px; }

.tab-bar    { display:flex; gap:4px; margin-bottom:16px; border-bottom:2px solid #e4eaf2; }
.tab-btn    { background:transparent; border:none; padding:10px 18px; font-size:13.5px; font-weight:600; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; }
.tab-btn--active { color:#004AAD; border-bottom-color:#004AAD; }

.section-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.section-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
.section-head h6 { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }

.detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.detail-item { display:flex; flex-direction:column; gap:4px; }
.detail-item--full { grid-column:1/-1; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.detail-value { font-size:13.5px; color:#1a2b45; }
.detail-link  { font-size:13.5px; color:#004AAD; text-decoration:none; }
.cats-row     { display:flex; flex-wrap:wrap; gap:6px; }
.cat-badge    { display:inline-block; background:#f0f5ff; color:#004AAD; font-size:11.5px; font-weight:600; border-radius:12px; padding:3px 10px; }

.empty-mini  { text-align:center; padding:32px; color:#aab8cc; font-size:13px; }

.contact-list { display:flex; flex-direction:column; gap:12px; }
.contact-card { display:flex; align-items:center; gap:14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; }
.contact-avatar { width:40px; height:40px; border-radius:50%; background:#004AAD; color:#fff; font-size:14px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.contact-info { flex:1; min-width:0; }
.contact-name { font-size:13.5px; font-weight:600; color:#1a2b45; }
.contact-role { font-size:12px; color:#7a90b0; }
.contact-meta { font-size:12px; color:#94a3b8; margin-top:2px; }
.contact-actions { display:flex; gap:6px; }
.primary-tag  { background:#dcfce7; color:#15803d; font-size:10px; font-weight:700; border-radius:10px; padding:2px 7px; margin-left:6px; vertical-align:middle; }

.doc-list { display:flex; flex-direction:column; gap:10px; }
.doc-row  { display:flex; align-items:center; gap:14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; }
.doc-icon { font-size:20px; color:#004AAD; flex-shrink:0; }
.doc-info { flex:1; min-width:0; }
.doc-name { font-size:13.5px; font-weight:600; color:#1a2b45; }
.doc-meta { font-size:12px; color:#7a90b0; margin-top:2px; }
.doc-expiry { margin-left:auto; }
.exp-badge { font-size:11.5px; font-weight:600; border-radius:12px; padding:3px 10px; }
.exp-badge--valid         { background:#dcfce7; color:#15803d; }
.exp-badge--expiring_soon { background:#fef9c3; color:#854d0e; }
.exp-badge--expired       { background:#fee2e2; color:#dc2626; }

.status-badge { display:inline-block; font-size:12px; font-weight:600; border-radius:20px; padding:4px 12px; }
.status-badge--prospect     { background:#f1f5f9; color:#64748b; }
.status-badge--registered   { background:#eff6ff; color:#1d4ed8; }
.status-badge--due_diligence { background:#fff7ed; color:#c2410c; }
.status-badge--qualified    { background:#f0fdf4; color:#16a34a; }
.status-badge--approved     { background:#dcfce7; color:#15803d; }
.status-badge--suspended    { background:#fef9c3; color:#854d0e; }
.status-badge--blocked      { background:#fee2e2; color:#dc2626; }

.risk-badge          { display:inline-block; font-size:11px; font-weight:700; border-radius:4px; padding:3px 8px; text-transform:uppercase; }
.risk-badge--low     { background:#dcfce7; color:#15803d; }
.risk-badge--medium  { background:#fef9c3; color:#854d0e; }
.risk-badge--high    { background:#fee2e2; color:#dc2626; }

.btn-primary  { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-sm       { padding:7px 14px; font-size:12.5px; }
.btn-outline  { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }

.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box      { background:#fff; border-radius:14px; width:100%; max-width:480px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm  { max-width:460px; }
.modal-box--lg  { max-width:680px; }
.modal-header   { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body     { padding:24px; overflow-y:auto; flex:1; }
.modal-footer   { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }

.field-grid     { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.field-full     { grid-column:1/-1; }
.field-group    { display:flex; flex-direction:column; gap:5px; }
.field-label    { font-size:13px; font-weight:600; color:#1a2b45; }
.req            { color:#e03535; }
.optional       { font-size:11px; color:#aab8cc; font-weight:400; }
.field-input    { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; font-family:inherit; box-sizing:border-box; transition:border-color .15s; }
.field-input:focus  { border-color:#004AAD; }
.field-input--error { border-color:#e03535; }
.field-error    { font-size:11.5px; color:#e03535; }
.field-select   { appearance:auto; cursor:pointer; }
.checkbox-label { display:flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.cat-checkboxes { display:flex; flex-wrap:wrap; gap:8px; }
.cat-checkbox   { display:inline-flex; align-items:center; gap:5px; font-size:13px; color:#1a2b45; cursor:pointer; background:#f8fafc; border:1px solid #e4eaf2; border-radius:6px; padding:5px 10px; }
.mt-3           { margin-top:14px; }
.me-1           { margin-right:4px; }

.doc-actions    { display:flex; gap:6px; flex-shrink:0; }
.btn-icon       { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:all .15s; text-decoration:none; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--view:hover  { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

@media(max-width:600px) {
  .profile-header { flex-direction:column; align-items:flex-start; }
  .profile-actions { width:100%; }
  .detail-grid { grid-template-columns:1fr; }
  .detail-item--full { grid-column:1; }
}
</style>
